import { useMutation } from '@apollo/client/react';
import { useCallback, useState } from 'react';

import { displayName, EDIT_PERSON, type Person } from '@/account/arcade-api';
import { ProfileCard } from '@/components/ProfileCard/ProfileCard';
import { SettingsHeading } from '@/components/SettingsHeading/SettingsHeading';
import { SettingsForm, type SettingsValues } from '@/components/SettingsForm/SettingsForm';
import { useToast } from '@/toast/useToast';

/** Edits the Person's name and business name through Arcade's `editPerson`. */
export function SettingsContainer({ person }: { person: Person }) {
  const toast = useToast();
  const [error, setError] = useState<string | null>(null);
  const [editPerson, { loading }] = useMutation(EDIT_PERSON);

  const initial: SettingsValues = {
    firstName: person.firstName ?? '',
    lastName: person.lastName ?? '',
    businessName: person.businessName ?? '',
  };

  const onSave = useCallback(
    async (values: SettingsValues) => {
      setError(null);
      try {
        await editPerson({ variables: values });
        toast.success({ title: 'Settings saved' });
      } catch {
        setError('Could not save your changes. Try again.');
      }
    },
    [editPerson, toast],
  );

  return (
    <>
      <SettingsHeading
        title="Profile"
        description="How you appear on the LendWell developer platform."
      />
      <SettingsForm
        // Re-seeds the form from the saved Person, so a save resets "dirty".
        key={JSON.stringify(initial)}
        email={person.email}
        initial={initial}
        pending={loading}
        error={error}
        onSave={onSave}
        aside={
          <ProfileCard
            name={displayName(person)}
            email={person.email}
            businessName={person.businessName}
          />
        }
      />
    </>
  );
}
